import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { useCache } from '../../context/CacheContext';
import { cachedPayerService, cachedPatientService } from '../../services/cachedApiService';
import { PriorAuthorization, Patient } from '../../types';
import '../../styles/ReviewAuthorization.css';

const ReviewAuthorization: React.FC = () => {
  const { authId } = useParams<{ authId: string }>();
  const [authorization, setAuthorization] = useState<PriorAuthorization | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'approved' | 'rejected'>('approved');
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showLetterModal, setShowLetterModal] = useState<boolean>(false);
  const [letterData, setLetterData] = useState<string | null>(null);
  const [letterLoading, setLetterLoading] = useState<boolean>(false);
  const [letterError, setLetterError] = useState<string | null>(null);
  const [aiSuggestion, setAiSuggestion] = useState<{
    decision: 'approved' | 'rejected';
    confidence: number;
    reason: string;
    source?: 'rag' | 'fallback' | 'metadata';  // Track the source of the suggestion
  } | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || user.role !== 'payer' || !authId) {
        setError('Unauthorized or invalid request');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // Fetch the specific authorization
        const pendingAuths = await cachedPayerService.getPendingAuthorizations();
        const auth = pendingAuths.find(a => a.id === parseInt(authId));
        
        if (!auth) {
          setError('Authorization not found or already reviewed');
          setLoading(false);
          return;
        }
        
        setAuthorization(auth);
        
        // Generate AI suggestion based on authorization data
        generateAiSuggestion(auth);
        
        // Fetch patient details
        try {
          const patientDetail = await cachedPatientService.getPatientDetail(auth.patient);
          setPatient(patientDetail.patient);
        } catch (err) {
          console.error('Error fetching patient details:', err);
          // Continue even if patient details can't be fetched
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching authorization details:', err);
        setError('Failed to fetch authorization details');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [user, authId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!authorization) return;
    
    try {
      setSubmitting(true);
      
      // Submit the review
      await cachedPayerService.reviewAuthorization(
        authorization.id,
        status,
        status === 'rejected' ? rejectionReason : undefined
      );
      
      // Invalidate related caches
      invalidate.priorAuths();
      
      setSuccessMessage(`Authorization has been ${status}. Redirecting...`);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/payer/pending-authorizations');
      }, 2000);
    } catch (err) {
      console.error('Error submitting review:', err);
      setError('Failed to submit review. Please try again.');
      setSubmitting(false);
    }
  };
  
  // Function to generate AI suggestions based on authorization data
  const generateAiSuggestion = (auth: PriorAuthorization) => {
    // Initial loading state
    setAiSuggestion({
      decision: 'approved',
      confidence: 50,
      reason: 'Analyzing prior authorization request...',
      source: 'fallback'
    });
    
    // Directly call the analyze endpoint with the letter ID
    fetch(`/api/analyze-pa-letter/${auth.id}/`, {
      credentials: 'include',
      headers: { Accept: 'application/json' }
    })
      .then(response => {
        if (!response.ok) {
          // Check if it's a 403 Forbidden error (access denied)
          if (response.status === 403) {
            throw new Error('Letter access denied');
          }
          throw new Error('Failed to analyze letter');
        }
        return response.json();
      })
      .then(analysisResult => {
        // Update the AI suggestion with the RAG analysis result
        setAiSuggestion({
          decision: analysisResult.decision,
          confidence: analysisResult.confidence,
          reason: analysisResult.reason,
          source: 'rag'  // Mark this as coming from the RAG system
        });
      })
      .catch(err => {
        console.error('Error in RAG analysis:', err);
        
        // Special handling for letter not found or access denied
        if (err.message === 'Letter access denied') {
          setAiSuggestion({
            decision: 'approved',
            confidence: 60,
            reason: 'LETTER NOT FOUND: Unable to access the PA letter for detailed analysis. This suggestion is based on the available request metadata only. Consider reviewing the letter manually before making a final decision.',
            source: 'metadata'  // Mark this as based on metadata only
          });
          return;
        }
        
        // Fallback to a default suggestion if RAG analysis fails
        if (auth.urgency === 'urgent') {
          setAiSuggestion({
            decision: 'approved',
            confidence: 85,
            reason: 'High urgency cases typically require immediate approval. Clinical reason appears valid based on historical patterns.',
            source: 'fallback'  // Mark this as a fallback suggestion
          });
        } else if (auth.clinical_reason && auth.clinical_reason.toLowerCase().includes('chronic')) {
          setAiSuggestion({
            decision: 'approved',
            confidence: 78,
            reason: 'Chronic condition management typically meets approval criteria based on standard guidelines.',
            source: 'fallback'  // Mark this as a fallback suggestion
          });
        } else {
          setAiSuggestion({
            decision: 'approved',
            confidence: 65,
            reason: 'Based on similar past authorizations, this request appears to meet standard approval criteria.',
            source: 'fallback'  // Mark this as a fallback suggestion
          });
        }
      });
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading authorization details...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="error-container">
          <div className="message-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="message-text">{error}</div>
          <div className="message-actions" style={{ marginTop: '15px' }}>
            <button
              className="retry-button"
              style={{
                padding: '8px 16px',
                backgroundColor: '#3182ce',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
              onClick={() => {
                invalidate.priorAuths();
                invalidate.patients();
                window.location.reload();
              }}
            >
              Retry
            </button>
            <button
              className="back-button"
              onClick={() => navigate('/payer/pending-authorizations')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Back to Pending Authorizations
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  if (successMessage) {
    return (
      <Layout>
        <div className="success-container">
          <div className="message-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M22 11.0857V12.0057C21.9988 14.1621 21.3005 16.2604 20.0093 17.9875C18.7182 19.7147 16.9033 20.9782 14.8354 21.5896C12.7674 22.201 10.5573 22.1276 8.53447 21.3803C6.51168 20.633 4.78465 19.2518 3.61096 17.4428C2.43727 15.6338 1.87979 13.4938 2.02168 11.342C2.16356 9.19029 2.99721 7.14205 4.39828 5.5028C5.79935 3.86354 7.69279 2.72111 9.79619 2.24587C11.8996 1.77063 14.1003 1.98806 16.07 2.86572" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="message-text">{successMessage}</div>
        </div>
      </Layout>
    );
  }

  if (!authorization) {
    return (
      <Layout>
        <div className="error-container">
          <div className="message-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="message-text">Authorization not found</div>
          <button
            className="back-button"
            onClick={() => navigate('/payer/pending-authorizations')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Pending Authorizations
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="review-auth-container">
        <h2 className="review-auth-title">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
            <path d="M14 3V7C14 7.55 14.45 8 15 8H19" stroke="currentColor" strokeWidth="2" />
            <path d="M17 21H7C5.90 21 5 20.10 5 19V5C5 3.89 5.90 3 7 3H14L19 8V19C19 20.10 18.10 21 17 21Z" stroke="currentColor" strokeWidth="2" />
          </svg>
          Review Authorization
        </h2>

        <div className="auth-card">
          <h3 className="auth-card-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Authorization Details
          </h3>
          
          <div className="detail-row">
            <div className="detail-col">
              <div className="detail-item">
                <div className="detail-label">Patient ID</div>
                <div className="detail-value">{authorization.patient}</div>
              </div>
              {patient && (
                <div className="detail-item">
                  <div className="detail-label">Patient Name</div>
                  <div className="detail-value">{patient.fname} {patient.lname}</div>
                </div>
              )}
              <div className="detail-item">
                <div className="detail-label">Request Type</div>
                <div className="detail-value">{authorization.request_type.charAt(0).toUpperCase() + authorization.request_type.slice(1)}</div>
              </div>
            </div>
            <div className="detail-col">
              <div className="detail-item">
                <div className="detail-label">Urgency</div>
                <div className="detail-value">
                  <span className={`badge ${authorization.urgency === 'urgent' ? 'warning' : 'normal'}`}>
                    {authorization.urgency.charAt(0).toUpperCase() + authorization.urgency.slice(1)}
                  </span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Created Date</div>
                <div className="detail-value">{authorization.created_at?.split(' ')[0].slice(0,10)}</div>
              </div>
              <div className="detail-item">
                <div className="detail-label">Reference ID</div>
                <div className="detail-value">{authorization.reference_id || 'N/A'}</div>
              </div>
            </div>
          </div>
          
          <div className="detail-item">
            <div className="detail-label">Clinical Reason</div>
            <div className="clinical-reason">
              {authorization.clinical_reason || 'No clinical reason provided'}
            </div>
          </div>
          
          <div className="detail-item">
            <div className="detail-label">Authorization Letter</div>
            <div className="detail-value">
              <button
                className="btn btn-view-letter"
                onClick={() => {
                  setLetterLoading(true);
                  setLetterError(null);
                  setLetterData(null);
                  setShowLetterModal(true);
                  
                  fetch(`/api/pa-letter-data/${authorization.id}/`, {
                    credentials: 'include',
                    headers: { Accept: 'application/json' }
                  })
                    .then(response => {
                      if (!response.ok) {
                        throw new Error('Failed to fetch letter data');
                      }
                      return response.json();
                    })
                    .then(data => {
                      setLetterData(data.data);
                      setLetterLoading(false);
                    })
                    .catch(err => {
                      console.error('Error fetching letter:', err);
                      setLetterError('Failed to load the letter. Please try again.');
                      setLetterLoading(false);
                    });
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 10V16M12 16L9 13M12 16L15 13M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                View Letter
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="review-form">
          <div className="auth-card">
            <h3 className="auth-card-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Review Decision
            </h3>
            
            {/* AI Suggestion Section */}
            {aiSuggestion && (
              <div className="ai-suggestion-container">
                <div className="ai-suggestion-header">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16V16.01M12 8V12M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
                      stroke={aiSuggestion.decision === 'approved' ? "#0d9488" : "#e11d48"}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"/>
                  </svg>
                  <span className="ai-suggestion-title">AI Suggestion</span>
                  <span className={`ai-confidence ${aiSuggestion.confidence >= 80 ? 'high' : aiSuggestion.confidence >= 70 ? 'medium' : 'low'}`}>
                    {aiSuggestion.confidence}% confidence
                  </span>
                  {aiSuggestion.source && (
                    <span className={`ai-source ${aiSuggestion.source}`} style={{
                      marginLeft: '10px',
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: aiSuggestion.source === 'rag' ? '#e0f2fe' :
                                      aiSuggestion.source === 'metadata' ? '#fef3c7' : '#f3f4f6',
                      color: aiSuggestion.source === 'rag' ? '#0369a1' :
                             aiSuggestion.source === 'metadata' ? '#92400e' : '#4b5563'
                    }}>
                      {aiSuggestion.source === 'rag' ? 'AI Analysis' :
                       aiSuggestion.source === 'metadata' ? 'Limited Data' : 'Fallback'}
                    </span>
                  )}
                </div>
                <div className={`ai-suggestion-decision ${aiSuggestion.decision === 'approved' ? 'approve' : 'reject'}`}>
                  Recommendation: <strong>{aiSuggestion.decision === 'approved' ? 'Approve' : 'Reject'}</strong>
                </div>
                <div className="ai-suggestion-reason">
                  {aiSuggestion.reason}
                </div>
                <div className="ai-suggestion-disclaimer">
                  This is an AI-generated suggestion to assist in decision making. Please review all clinical information before making a final determination.
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Decision</label>
              <div className="radio-group">
                <div className="radio-option">
                  <input
                    type="radio"
                    name="status"
                    id="approve"
                    value="approved"
                    checked={status === 'approved'}
                    onChange={() => setStatus('approved')}
                  />
                  <label htmlFor="approve">Approve</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    name="status"
                    id="reject"
                    value="rejected"
                    checked={status === 'rejected'}
                    onChange={() => setStatus('rejected')}
                  />
                  <label htmlFor="reject">Reject</label>
                </div>
              </div>
            </div>
            
            {status === 'rejected' && (
              <div className="form-group">
                <label htmlFor="rejectionReason" className="form-label">Rejection Reason (Required)</label>
                <textarea
                  className="textarea-field"
                  id="rejectionReason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a detailed reason for rejection..."
                  required
                ></textarea>
              </div>
            )}
            
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate('/payer/pending-authorizations')}
                disabled={submitting}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Cancel
              </button>
              <button
                type="submit"
                className={`btn ${status === 'approved' ? 'btn-success' : 'btn-danger'}`}
                disabled={submitting || (status === 'rejected' && !rejectionReason.trim())}
              >
                {submitting ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="animate-spin">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="60 30"/>
                    </svg>
                    Submitting...
                  </>
                ) : status === 'approved' ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Approve Authorization
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Reject Authorization
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
      
      {/* Letter Modal */}
      {showLetterModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}>
            <div className="modal-header" style={{
              padding: '16px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ fontSize: '1.2rem', margin: '0' }}>Prior Authorization Letter</h3>
              <button
                onClick={() => setShowLetterModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px'
                }}
                aria-label="Close modal"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
            </div>
            <div className="pdf-container" style={{
              padding: '16px',
              flex: 1,
              overflow: 'auto',
              minHeight: '450px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              {letterLoading && (
                <div style={{ textAlign: 'center', padding: '30px 0' }}>
                  <div className="loading-spinner" style={{ width: '40px', height: '40px', margin: '0 auto 15px' }}></div>
                  <div style={{ fontSize: '0.9rem' }}>Loading letter...</div>
                </div>
              )}
              {letterError && (
                <div style={{ color: '#e53e3e', textAlign: 'center', padding: '20px' }}>
                  {letterError}
                </div>
              )}
              {letterData && !letterLoading && !letterError && (
                <iframe
                  src={`data:application/pdf;base64,${letterData}`}
                  title="PA Letter"
                  width="100%"
                  height="450px"
                  style={{ border: '1px solid #edf2f7', borderRadius: '6px' }}
                />
              )}
            </div>
            <div className="modal-footer" style={{
              padding: '16px',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px'
            }}>
              <button
                className="btn btn-primary"
                onClick={() => window.print()}
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                  <path d="M6 9V2H18V9" stroke="currentColor" strokeWidth="2" />
                  <path d="M6 18H4C3.47 18 2.96 17.79 2.59 17.41C2.21 17.04 2 16.53 2 16V11C2 10.47 2.21 9.96 2.59 9.59C2.96 9.21 3.47 9 4 9H20C20.53 9 21.04 9.21 21.41 9.59C21.79 9.96 22 10.47 22 11V16C22 16.53 21.79 17.04 21.41 17.41C21.04 17.79 20.53 18 20 18H18" stroke="currentColor" strokeWidth="2" />
                  <path d="M18 14H6V22H18V14Z" stroke="currentColor" strokeWidth="2" />
                </svg>
                Print
              </button>
              <button
                className="btn btn-cancel"
                onClick={() => setShowLetterModal(false)}
                style={{ padding: '6px 12px', fontSize: '12px' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginRight: '4px' }}>
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" />
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ReviewAuthorization;